import React, { useEffect, useRef } from 'react';
import { useAnalytics } from '../../../utils/analytics';
import { BaseStepPropsV2 } from '../../common/v2/BaseStepV2.types';
import { StepV2ConfirmButton } from '../../common/v2/StepV2ConfirmButton';
import { MainButtonVariant } from '../../styledComponent/Buttons.style';
import {
  LetsHealTogetherScreen,
  LetsHealTogetherContentWrapper,
  LetsHealTogetherCtaWrapper,
  LetsHealTogetherTitle,
  LetsHealTogetherCtaContainer,
  LetsHealTogetherHighlightsWrapper,
  LetsHealTogetherIntroContentWrapper,
} from './LetsHealTogether.style';
import { AbsoluteHeader } from '../../common/AbsoluteHeader/AbsoluteHeader';
import { useMst } from '../../../models/rootModel';
import { BaseStepV3 } from '../../common/v3/BaseStepV3';
import { LetsHealTogetherHighlight } from './LetsHealTogetherHighlight';
import gsap from 'gsap';
import { TabletWidth } from '../../../global.style';
import { useIsMobileWebview } from '../../../hooks/useIsMobileWebview.hook';

export interface Highlight {
  text: string;
  iconUrl: string;
}
interface LetsHealTogetherStepProps extends BaseStepPropsV2 {
  title: string;
  secondTitle: string;
  highlights: Highlight[];
  ctaButtonTitle: string;
  backgroundImageUrl: string;
  mobileViewBackgroundImageUrl: string;
  nextStepId?: string;
}

export const LetsHealTogether: React.FC<LetsHealTogetherStepProps> = (props) => {
  const {
    nextStep,
    goToNamedStep,
    ctaButtonTitle,
    isActive,
    backgroundImageUrl,
    mobileViewBackgroundImageUrl,
    previousStep,
    nextStepId,
    stepName,
    secondTitle,
    highlights,
    title,
  } = props;

  const analytics = useAnalytics();
  const { UserDiscoveryResponse, PlanLabels } = useMst();

  const introContentWrapperRef = useRef(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const highlightsWrapperRef = useRef<HTMLDivElement>(null);
  const ctaWrapperRef = useRef(null);
  const secondTitleRef = useRef<HTMLHeadingElement>(null);

  const isMobileWebview = useIsMobileWebview();

  useEffect(() => {
    if (isActive) {
      analytics.track(`view letsHealTogether`, { stepName, segment: UserDiscoveryResponse.segment, isMobileWebview });
      window.scrollTo(0, 0);

      const mm = gsap.matchMedia();
      const ctx = gsap.context(() => {
        const tl = gsap.timeline();

        //Initial state
        tl.set(introContentWrapperRef.current, {
          display: 'block',
        });
        tl.set(contentWrapperRef.current, {
          opacity: 0,
          display: 'none',
        });
        Array.from(highlightsWrapperRef.current?.children ?? []).forEach((child) => {
          tl.set(child, {
            opacity: 0,
          });
        });
        tl.set(ctaWrapperRef.current, {
          opacity: 0,
          pointerEvents: 'none',
        });
        tl.set(secondTitleRef.current, {
          position: 'relative',
          opacity: 0,
        });

        //Intro
        tl.fromTo(
          introContentWrapperRef.current,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 1,
            ease: 'power2.out',
            delay: 0.75,
          }
        );

        tl.to(introContentWrapperRef.current, {
          opacity: 0,
          duration: 0.5,
          ease: 'power2.out',
          delay: 2,
        });

        //Transition from intro to content
        tl.set(introContentWrapperRef.current, {
          display: 'none',
        });

        mm.add(`(max-width: ${TabletWidth}px)`, () => {
          tl.set(contentWrapperRef.current, {
            display: 'flex',
          });
        });
        mm.add(`(min-width: ${TabletWidth}px)`, () => {
          tl.set(contentWrapperRef.current, {
            display: 'block',
          });
        });

        //Content
        tl.to(contentWrapperRef.current, {
          opacity: 1,
          duration: 0,
        });

        //Set second title position
        tl.to(secondTitleRef.current, {
          duration: 0,
          top: () => {
            const secondContentHeight = contentWrapperRef.current!.getBoundingClientRect().height;

            const screenWidth = window.innerWidth;

            return screenWidth >= TabletWidth ? `${secondContentHeight / 2 - 28}px` : `35%`;
          },
        });

        //Fade in second title
        tl.to(secondTitleRef.current, {
          opacity: 1,
          duration: 1,
          delay: 0.25,
          ease: 'power2.out',
        });

        //Move second title to top
        tl.to(secondTitleRef.current, {
          top: () => {
            const screenWidth = window.innerWidth;

            return screenWidth >= TabletWidth ? '0px' : '0%';
          },
          duration: 1,
          ease: 'power2.inOut',
          delay: 1,
        });

        //Fade in highlights
        Array.from(highlightsWrapperRef.current?.children ?? []).forEach((child) => {
          tl.fromTo(
            child,
            {
              opacity: 0,
            },
            {
              opacity: 1,
              duration: 1,
              ease: 'power2.out',
            },
            '-=0.5'
          );
        });

        tl.to(
          ctaWrapperRef.current,
          {
            opacity: 1,
            duration: 1,
            ease: 'power2.out',
            pointerEvents: 'auto',
          },
          '-=0.5'
        );
      });

      return () => {
        ctx.kill();
        mm.kill();
      };
    }
  }, [analytics, isActive, UserDiscoveryResponse.segment, stepName, isMobileWebview]);

  const handleOnClick = () => {
    analytics.track(`click letsHealTogether`, { stepName, segment: UserDiscoveryResponse.segment, isMobileWebview });

    if (isMobileWebview && window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ action: 'continue', onboardingData: PlanLabels.selectedPlanLabelsObject })
      );
      return;
    }

    if (nextStepId) {
      goToNamedStep(nextStepId);
    } else {
      nextStep();
    }
  };

  const onBack = () => {
    previousStep();
  };

  return (
    <BaseStepV3 {...props}>
      <LetsHealTogetherScreen imgDesktop={backgroundImageUrl} imgMobile={mobileViewBackgroundImageUrl}>
        <AbsoluteHeader onBack={onBack} color="white" withLogoOnMobileDisplay logoColor="white" />

        <LetsHealTogetherIntroContentWrapper ref={introContentWrapperRef}>
          <LetsHealTogetherTitle isMain>{title}</LetsHealTogetherTitle>
        </LetsHealTogetherIntroContentWrapper>
        <LetsHealTogetherContentWrapper ref={contentWrapperRef}>
          <LetsHealTogetherTitle ref={secondTitleRef}>{secondTitle}</LetsHealTogetherTitle>
          <LetsHealTogetherHighlightsWrapper ref={highlightsWrapperRef}>
            {highlights &&
              highlights.map(({ text, iconUrl }, index) => (
                <LetsHealTogetherHighlight key={index} {...{ text, iconUrl }} />
              ))}
          </LetsHealTogetherHighlightsWrapper>
          <LetsHealTogetherCtaContainer ref={ctaWrapperRef}>
            <LetsHealTogetherCtaWrapper>
              <StepV2ConfirmButton
                {...props}
                isButtonDisabled={false}
                isLoading={false}
                onClick={handleOnClick}
                ctaButtonTitle={ctaButtonTitle}
                variant={MainButtonVariant.White}
              />
            </LetsHealTogetherCtaWrapper>
          </LetsHealTogetherCtaContainer>
        </LetsHealTogetherContentWrapper>
      </LetsHealTogetherScreen>
    </BaseStepV3>
  );
};
